import React from "react";
import { Button, ButtonGroup } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Box, Stack, Typography } from "@mui/material";
import { useCompactLayout } from "../../../components/responsive";

export function ClosureListToolbar({
  dateFilter,
  dateFrom,
  dateTo,
  searchKey,
  activeFilterLabel,
  onFilterChange,
  onDateFromChange,
  onDateToChange,
  onSearch,
  onClear,
  renderDatePickerHeader,
}: {
  dateFilter: string;
  dateFrom: any;
  dateTo: any;
  searchKey: string;
  activeFilterLabel: string;
  onFilterChange: (filter: string) => void;
  onDateFromChange: (date: any) => void;
  onDateToChange: (date: any) => void;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  renderDatePickerHeader: (props: any) => React.ReactNode;
}) {
  const { containerRef, isCompact } = useCompactLayout();
  const showClear = dateFilter !== "all" || Boolean(searchKey);

  return (
    <Box ref={containerRef} sx={{ width: "100%", minWidth: 0 }}>
      <Typography className="card-title mb-3" variant="h6" sx={{ mb: 1.5 }}>
        Retreat Closures
      </Typography>
      <Stack
        direction={isCompact ? "column" : "row"}
        spacing={1}
        alignItems={isCompact ? "stretch" : "center"}
        flexWrap="wrap"
        useFlexGap
        sx={{
          width: "100%",
          minWidth: 0,
        }}
      >
        <ButtonGroup
          size="sm"
          className={isCompact ? "w-100 flex-wrap" : undefined}
        >
          <Button
            variant={dateFilter === "all" ? "primary" : "outline-primary"}
            onClick={() => onFilterChange("all")}
          >
            All Retreats
          </Button>
          <Button
            variant={dateFilter === "current" ? "primary" : "outline-primary"}
            onClick={() => onFilterChange("current")}
          >
            Current Month
          </Button>
          <Button
            variant={dateFilter === "previous" ? "primary" : "outline-primary"}
            onClick={() => onFilterChange("previous")}
          >
            Previous Month
          </Button>
        </ButtonGroup>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            flexShrink: 0,
            width: isCompact ? "100%" : "auto",
            "& .react-datepicker-wrapper": {
              flex: isCompact ? "1 1 140px" : "0 0 auto",
              minWidth: isCompact ? 0 : 120,
            },
            "& .form-control": { minWidth: 0, width: "100%" },
          }}
        >
          <DatePicker
            selected={dateFrom}
            onChange={onDateFromChange}
            dateFormat="MM-dd-yyyy"
            placeholderText="From Date"
            className="form-control form-control-sm"
            maxDate={dateTo || new Date()}
            isClearable
            showDisabledMonthNavigation
            renderCustomHeader={renderDatePickerHeader}
          />
          <span style={{ color: "#6c757d", flex: "0 0 auto" }}>to</span>
          <DatePicker
            selected={dateTo}
            onChange={onDateToChange}
            dateFormat="MM-dd-yyyy"
            placeholderText="To Date"
            className="form-control form-control-sm"
            minDate={dateFrom}
            maxDate={new Date()}
            isClearable
            showDisabledMonthNavigation
            renderCustomHeader={renderDatePickerHeader}
          />
        </Stack>

        <Box
          className="form-group content1"
          sx={{
            flex: isCompact ? "1 1 auto" : "0 0 240px",
            minWidth: 0,
            width: isCompact ? "100%" : 240,
            mb: "0 !important",
          }}
        >
          <div className="input-group setSearch">
            <input
              onChange={onSearch}
              type="text"
              className="form-control searchData"
              placeholder="Search by Retreat ID"
              aria-label="Search"
              value={searchKey}
            />
            <div className="input-group-append">
              <button className="btn btn-sm btn-primary" type="button">
                <i className="icon-search"></i>
              </button>
            </div>
          </div>
        </Box>

        {showClear && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={onClear}
            title="Clear filters"
            style={{ minWidth: "40px" }}
          >
            ✕
          </Button>
        )}
      </Stack>

      {dateFilter !== "all" && activeFilterLabel ? (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "#f8f9fa",
            borderRadius: 1,
            px: 1.5,
            py: 1,
            mt: 1.5,
          }}
        >
          <span className="badge bg-primary">{activeFilterLabel}</span>
          <small className="text-muted">Active Filter</small>
        </Box>
      ) : null}
    </Box>
  );
}
